// Owner console shell (D-066, owner 2026-10-25: "reconstruct the console like the vendor dashboard"),
// built on the vendor admin's (apps/admin/src/shell, D-038/D-042):
//  - sidebar: the console box at the top, main links first with no heading, then headed groups;
//    33 px rows, 12 px labels, 16 px icons, grey at rest and the logo gradient when active;
//  - top bar: the logo, search (sections and shops, Ctrl K), the "Needs you" bell, the environment
//    and the account menu (theme, sign out).
// No group section bar above the page (owner, 2026-10-27): the sidebar already lists every section.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { i18n, t } from "@ady/i18n";
import {
  Badge,
  CommandPalette,
  cx,
  Drawer,
  Icon,
  Logo,
  usePaletteShortcut,
  type IconName,
  type PaletteCommand,
} from "@ady/ui";
import { api, vendorsConsoleApi, type StaffMe } from "./api";
import { OVERVIEW_KEY } from "./overview";
import { GROUPS, SECTIONS, type ConsoleSection } from "./sections";
import { applyTheme, readTheme, type ThemeChoice } from "./theme";

export const SHELL_TID = {
  sidebar: "console-sidebar",
  topBar: "console-top-bar",
  search: "console-search",
  attention: "console-attention",
  account: "console-account",
} as const;

const go = (router: ReturnType<typeof useRouter>, href: string) => router.history.push(href);

export function sectionFor(pathname: string): ConsoleSection | undefined {
  return SECTIONS.find((s) => (s.path === "/" ? pathname === "/" : pathname.startsWith(s.path)));
}

const ATTENTION_ICON: Record<string, IconName> = {
  topups: "wallet",
  kyc: "shieldCheck",
  import_payments: "ship",
  import_requests: "ship",
  sms: "chat",
  ads: "megaphone",
  tickets: "lifebuoy",
  ideas: "sparkle",
  reports: "flag",
  at_risk: "pulse",
  jobs: "jobs",
};

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .map((p) => Array.from(p)[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

// ---------------------------------------------------------------- sidebar

function ConsoleBox({ me }: { me: StaffMe }) {
  return (
    <div className="hidden h-18 w-full flex-none items-center gap-3 border-b border-subtle px-4 lg:flex">
      <span
        className="flex size-9 flex-none items-center justify-center rounded-full ady-bg-brand-tint"
        aria-hidden="true"
      >
        <Logo variant="mark" height={18} decorative />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{t("console.shell.title")}</span>
        <span className="truncate text-2xs text-fg-tertiary uppercase">{me.staff.role ?? ""}</span>
      </span>
    </div>
  );
}

function NavRows({ onPick }: { onPick?: () => void }) {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = sectionFor(pathname)?.id;
  return (
    <ul className="ady-font-inter ady-scroll-quiet flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3 lg:gap-0">
      {GROUPS.map((g) => {
        const items = SECTIONS.filter((s) => s.group === g);
        if (!items.length) return null;
        return (
          <Fragment key={g}>
            {g !== "main" ? (
              <li
                aria-hidden="true"
                className="px-2 pt-3 pb-0.5 text-xs font-medium text-fg-tertiary"
              >
                {i18n.tDynamic(`console.navSections.${g}`, "console.shell.sections")}
              </li>
            ) : null}
            {items.map((s) => {
              const active = s.id === current;
              return (
                <li key={s.id}>
                  <a
                    href={s.path}
                    onClick={(e) => {
                      e.preventDefault();
                      go(router, s.path);
                      onPick?.();
                    }}
                    aria-current={active ? "page" : undefined}
                    data-testid={`console-nav-${s.id}`}
                    className={cx(
                      "flex min-h-11 w-full items-center gap-3 rounded-sm px-2 text-md font-medium no-underline transition-colors duration-(--ady-dur-quick) lg:min-h-0 lg:rounded-xs lg:py-1.5 lg:text-xs lg:font-semibold",
                      active ? "ady-bg-brand-tint text-fg" : "ady-hover-brand text-fg-secondary",
                    )}
                  >
                    <Icon
                      name={s.icon}
                      gradient={active}
                      className={cx("flex-none lg:size-4", !active && "text-fg-tertiary")}
                    />
                    <span className={cx("ady-hover-label truncate", active && "ady-text-brand")}>
                      {t(s.label)}
                    </span>
                    {s.pendingActivation ? (
                      <Icon name="clock" className="ms-auto size-3 flex-none text-fg-tertiary" />
                    ) : null}
                  </a>
                </li>
              );
            })}
          </Fragment>
        );
      })}
    </ul>
  );
}

export function Sidebar({ me }: { me: StaffMe }) {
  return (
    <nav
      aria-label={t("shell.mainNav")}
      data-testid={SHELL_TID.sidebar}
      className="sticky top-0 hidden h-dvh w-52 flex-none flex-col border-e border-subtle bg-surface lg:flex"
    >
      <ConsoleBox me={me} />
      <NavRows />
    </nav>
  );
}

/** Phones and tablets: the same rows in a drawer. */
export function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer open={open} onClose={onClose} title={t("console.shell.title")}>
      <NavRows onPick={onClose} />
    </Drawer>
  );
}

// ---------------------------------------------------------------- top bar

const iconBtn =
  "flex size-10 flex-none items-center justify-center rounded-full border border-card bg-surface transition-colors duration-(--ady-dur-quick) hover:border-link hover:bg-selected";

function AttentionBell() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => vendorsConsoleApi.overview(),
    retry: false,
    refetchInterval: 60_000,
  });
  const items = q.data?.attention ?? [];
  const count = items.reduce((n, a) => n + a.count, 0);
  return (
    <>
      <button
        type="button"
        className={cx(iconBtn, "relative")}
        aria-label={t("console.shell.attentionLabel", { count })}
        data-testid={SHELL_TID.attention}
        onClick={() => setOpen(true)}
      >
        <Icon name="bell" gradient />
        {count > 0 ? (
          <span className="absolute -end-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-danger-bg px-1 text-2xs font-semibold text-danger-fg">
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title={t("console.shell.attention")}>
        <div className="flex flex-col gap-2">
          {items.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-fg-secondary">
              <span className="text-success-fg">
                <Icon name="checkCircle" />
              </span>
              {t("console.shell.attentionNone")}
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {items.map((a) => (
                <li key={a.kind}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      go(router, a.path);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xs border border-card px-2.5 py-2 text-start hover:bg-hover"
                  >
                    <span className="flex size-7 flex-none items-center justify-center rounded-xs bg-warning-bg text-warning-fg">
                      <Icon name={ATTENTION_ICON[a.kind] ?? "inbox"} />
                    </span>
                    <span className="flex-1 text-sm font-medium">
                      {i18n.tDynamic(
                        `plans.overview.attention.${a.kind}`,
                        "plans.overview.attentionTitle",
                      )}
                    </span>
                    <span className="rounded-full bg-warning-bg px-2 text-xs font-bold text-warning-fg tabular-nums">
                      {a.count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className="w-fit text-sm font-medium text-link hover:underline"
            onClick={() => {
              setOpen(false);
              go(router, "/");
            }}
          >
            {t("console.shell.attentionAll")}
          </button>
        </div>
      </Drawer>
    </>
  );
}

function AccountMenu({ me }: { me: StaffMe }) {
  const router = useRouter();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const signOut = async () => {
    await api.logout().catch(() => undefined);
    qc.clear();
    go(router, "/sign-in");
  };
  const name = me.staff.display_name || me.staff.email;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={t("shell.accountMenu", { name })}
        data-testid={SHELL_TID.account}
        onClick={() => setOpen((o) => !o)}
        className="flex size-11 items-center justify-center rounded-full"
      >
        <span className="flex size-9 items-center justify-center rounded-full bg-action-bg text-sm font-semibold text-action-fg">
          {initials(name)}
        </span>
      </button>
      {open ? (
        <div className="absolute end-0 top-full z-(--ady-z-popover) mt-2 w-72 rounded-md border border-subtle bg-overlay p-2 shadow-2">
          <div className="px-3 py-2">
            <p className="text-md font-medium">{name}</p>
            <p className="text-sm text-fg-secondary">{me.staff.email}</p>
            <p className="flex flex-wrap items-center gap-1.5 pt-1.5">
              <Badge tone="success">{t("console.shell.mfaOn")}</Badge>
              {me.staff.role ? (
                <Badge tone="neutral" icon={null}>
                  {t("console.shell.role", { role: me.staff.role })}
                </Badge>
              ) : null}
            </p>
          </div>
          {/* D-081: the staff member's own authenticator apps and recovery codes. */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              go(router, "/account/security");
            }}
            data-testid="console-my-security"
            className="flex min-h-11 w-full items-center gap-3 rounded-sm border-t border-subtle px-3 text-md text-fg hover:bg-hover lg:min-h-control lg:text-sm"
          >
            <Icon name="shieldCheck" className="text-fg-secondary" />
            {t("console.mySecurity.title")}
          </button>
          <fieldset className="border-t border-subtle px-3 py-2">
            <legend className="py-1 text-xs font-medium text-fg-tertiary">
              {t("shell.theme")}
            </legend>
            {(["system", "light", "dark"] as const).map((choice) => (
              <label
                key={choice}
                className="flex min-h-11 cursor-pointer items-center gap-3 text-md lg:min-h-control lg:text-sm"
              >
                <input
                  type="radio"
                  name="ady-console-theme"
                  className="size-5 accent-(--ady-color-action-primary-bg) lg:size-4"
                  checked={theme === choice}
                  onChange={() => {
                    setTheme(choice);
                    applyTheme(choice);
                  }}
                />
                {t(
                  choice === "system"
                    ? "shell.themeSystem"
                    : choice === "light"
                      ? "shell.themeLight"
                      : "shell.themeDark",
                )}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            onClick={signOut}
            data-testid="console-sign-out"
            className="flex min-h-11 w-full items-center gap-3 rounded-sm border-t border-subtle px-3 text-md text-fg hover:bg-hover lg:min-h-control lg:text-sm"
          >
            <Icon name="logout" className="text-fg-secondary" />
            {t("common.signOut")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function usePalette() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  usePaletteShortcut(() => setOpen(true));
  const dir = useQuery({
    queryKey: ["console", "vendors"],
    queryFn: () => vendorsConsoleApi.directory(),
    enabled: open,
    retry: false,
  });
  const commands = useMemo<PaletteCommand[]>(
    () => [
      ...(dir.data?.rows ?? []).map((r) => ({
        id: `shop-${r.id}`,
        section: "records" as const,
        label: r.name,
        keywords: `${r.slug} ${r.owner_email ?? ""} ${r.plan.name}`,
        icon: "store" as const,
        run: () => go(router, `/vendors/${r.id}`),
      })),
      ...SECTIONS.map((s) => ({
        id: `go-${s.id}`,
        section: "goTo" as const,
        label: t(s.label),
        keywords: s.group,
        icon: s.icon,
        run: () => go(router, s.path),
      })),
    ],
    [dir.data, router],
  );
  return { open, setOpen, commands };
}

export function TopBar({ me, onMenu }: { me: StaffMe; onMenu: () => void }) {
  const palette = usePalette();
  const env = me.environment;
  return (
    <header
      data-testid={SHELL_TID.topBar}
      className="sticky top-0 z-(--ady-z-sticky) flex h-16 flex-none items-center gap-2 border-b border-subtle bg-surface/95 px-2 backdrop-blur md:px-4 lg:h-18"
    >
      <button
        type="button"
        className="flex size-11 items-center justify-center rounded-sm text-fg-secondary hover:bg-hover lg:hidden"
        aria-label={t("common.menu")}
        onClick={onMenu}
      >
        <Icon name="menu" />
      </button>
      {/* Phones get the mark only (as the vendor admin): the full lockup pushed the bell and the
          account button past a 375 px screen, so every page scrolled sideways. */}
      {/* Show/hide sits on wrappers: Logo and Badge set their own display, which beat `hidden`. */}
      <span className="inline-flex sm:hidden">
        <Logo variant="mark" height={28} />
      </span>
      <span className="hidden sm:inline-flex">
        <Logo variant="lockup" height={30} />
      </span>
      <span className="hidden sm:inline-flex">
        <Badge
          tone={env === "production" ? "danger" : env === "staging" ? "warning" : "neutral"}
          icon={null}
        >
          {i18n.tDynamic(`console.shell.env.${env}`, "console.shell.env.local")}
        </Badge>
      </span>
      <button
        type="button"
        onClick={() => palette.setOpen(true)}
        data-testid={SHELL_TID.search}
        aria-keyshortcuts="Control+K Meta+K /"
        aria-label={t("shell.search")}
        className={cx(iconBtn, "ms-auto")}
      >
        <Icon name="search" gradient />
      </button>
      <div className="flex items-center gap-1 lg:gap-2">
        <AttentionBell />
        <AccountMenu me={me} />
      </div>
      <CommandPalette
        open={palette.open}
        onOpenChange={palette.setOpen}
        commands={palette.commands}
        recordsNote={t("console.shell.searchNote")}
      />
    </header>
  );
}
